import type { Editor } from "@tiptap/react";
import { generateId } from "@/utils/id";

interface FloatBtnState {
  top: number;
  left: number;
  visible: boolean;
  insertPos: number;
}

interface FloatInsertMenuProps {
  floatBtn: FloatBtnState;
  setFloatBtn: React.Dispatch<React.SetStateAction<FloatBtnState>>;
  floatBtnElRef: React.RefObject<HTMLButtonElement | null>;
  hideTimerRef: React.RefObject<ReturnType<typeof setTimeout> | undefined>;
  pendingInsertPosRef: React.RefObject<number | null>;
  floatMenuOpen: boolean;
  setFloatMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setShowInsertPicker: React.Dispatch<React.SetStateAction<boolean>>;
  editorRef: React.RefObject<Editor | null>;
}

function handleInsertSection(
  editorRef: React.RefObject<Editor | null>,
  pendingInsertPosRef: React.RefObject<number | null>,
  setFloatMenuOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setFloatBtn: React.Dispatch<React.SetStateAction<FloatBtnState>>,
) {
  const editor = editorRef.current;
  if (editor) {
    const pos = pendingInsertPosRef.current ?? editor.state.selection.to;
    pendingInsertPosRef.current = null;
    editor
      .chain()
      .insertContentAt(pos, {
        attrs: { required: false, sectionId: generateId() },
        type: "section",
      })
      .focus()
      .run();
  }
  setFloatMenuOpen(false);
  setFloatBtn((p) => ({ ...p, visible: false }));
}

function handleInsertSpace(
  editorRef: React.RefObject<Editor | null>,
  pendingInsertPosRef: React.RefObject<number | null>,
  setFloatMenuOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setFloatBtn: React.Dispatch<React.SetStateAction<FloatBtnState>>,
) {
  const editor = editorRef.current;
  if (editor) {
    const pos = pendingInsertPosRef.current ?? editor.state.selection.to;
    pendingInsertPosRef.current = null;
    editor.chain().insertContentAt(pos, { type: "paragraph" }).focus().run();
  }
  setFloatMenuOpen(false);
  setFloatBtn((p) => ({ ...p, visible: false }));
}

function FloatButton({
  floatBtn,
  floatBtnElRef,
  floatMenuOpen,
  hideTimerRef,
  pendingInsertPosRef,
  setFloatBtn,
  setFloatMenuOpen,
}: Readonly<
  Pick<
    FloatInsertMenuProps,
    | "floatBtn"
    | "setFloatBtn"
    | "floatBtnElRef"
    | "hideTimerRef"
    | "pendingInsertPosRef"
    | "floatMenuOpen"
    | "setFloatMenuOpen"
  >
>) {
  if (!floatBtn.visible) {
    return null;
  }
  return (
    <button
      ref={floatBtnElRef}
      className="unified-float-insert-btn"
      style={{ left: floatBtn.left, top: floatBtn.top }}
      title="Insert here"
      onMouseEnter={() => {
        clearTimeout(hideTimerRef.current);
      }}
      onMouseLeave={() => {
        if (floatMenuOpen) {
          return;
        }
        hideTimerRef.current = setTimeout(() => {
          setFloatBtn((p) => ({ ...p, visible: false }));
        }, 250);
      }}
      onClick={() => {
        pendingInsertPosRef.current = floatBtn.insertPos;
        setFloatMenuOpen((p) => !p);
      }}
    >
      +
    </button>
  );
}

function FloatMenuItems(props: Readonly<FloatInsertMenuProps>) {
  return (
    <>
      <button
        className="unified-float-menu-item"
        onClick={() => {
          props.setFloatMenuOpen(false);
          props.setShowInsertPicker(true);
        }}
      >
        Insert Template
      </button>
      <button
        className="unified-float-menu-item"
        onClick={() => {
          handleInsertSection(
            props.editorRef,
            props.pendingInsertPosRef,
            props.setFloatMenuOpen,
            props.setFloatBtn,
          );
        }}
      >
        Insert Section
      </button>
      <button
        className="unified-float-menu-item"
        onClick={() => {
          handleInsertSpace(
            props.editorRef,
            props.pendingInsertPosRef,
            props.setFloatMenuOpen,
            props.setFloatBtn,
          );
        }}
      >
        Insert Space
      </button>
    </>
  );
}

function FloatMenuDropdown(props: Readonly<FloatInsertMenuProps>) {
  if (!props.floatMenuOpen) {
    return null;
  }
  return (
    <>
      <div
        style={{ inset: 0, position: "fixed", zIndex: 49 }}
        onClick={() => {
          props.setFloatMenuOpen(false);
        }}
      />
      <div
        className="unified-float-menu"
        style={{ left: props.floatBtn.left + 34, top: props.floatBtn.top - 4 }}
      >
        <FloatMenuItems {...props} />
      </div>
    </>
  );
}

export function FloatInsertMenu(props: Readonly<FloatInsertMenuProps>) {
  return (
    <>
      <FloatButton {...props} />
      <FloatMenuDropdown {...props} />
    </>
  );
}
